import { FC, Fragment, useState, useContext, useEffect, useRef } from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon   from '@mui/icons-material/ArrowDropUp';
import { AddStoreModal } from '../../../modals';
import ContextStore      from '../../../hooks/ContextStore';
import { IUserResponse } from '../../../interfaces/responses/UserResponse.interface';
import { useNavigate, useParams } from 'react-router-dom';
import { IStoreResponse } from '../../../interfaces/responses/StoreResponse.interface';
import { IErrorResponse } from '../../../interfaces/responses/ErrorResponse.interface';
import { dispatch }       from '../../../dispatch/dispatch';
import { actions }        from '../../../dispatch';
import { ILogResponse }   from '../../../interfaces/responses/LogResponse.interface';
import { IDeleteResponse }from '../../../interfaces/responses/DeleteResponse.interface';

// ─── ALL BUSINESS LOGIC IS 100% UNCHANGED ────────────────────────────────────
export const Header: FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { contextStore, setContextStore } = useContext(ContextStore);
  const [stores, setStores]                 = useState<Array<IStoreResponse>>([]);
  const [expandDropdown, setExpandDropdown] = useState<boolean>(false);
  const [showModal, setShowModal]           = useState<boolean>(false);
  const [logsUpdate, setLogsUpdate]         = useState<number>(0);
  const logsRef = useRef<Array<ILogResponse>>([]);

  const setLogs = (vLogs: Array<ILogResponse>) => {
    logsRef.current = vLogs;
    setLogsUpdate((past) => past + 1);
  };

  const onClickSmartAnalytics       = () => navigate('/smart-analytics');
  const onClickProductGroup         = () => navigate('/productgroup/update');
  const onClickProductGroupOrderDate= () => navigate('/productgroup/order');

  const onSelectStore = (storeId: string) => {
    if (id !== '0') contextStore.socket.emit('leaveStoreLogsRoom', [id]);
    setExpandDropdown(false);
    navigate(`/dashboard/${storeId}`);
  };

  const onClickLogout = () => {
    localStorage.removeItem('token');
    if (contextStore.socket.connected) contextStore.socket.disconnect();
    setContextStore((past: any) => ({ ...past, token: '', user: {} as IUserResponse }));
    window.location.reload();
  };

  const onClickReinitiate = async () => {
    contextStore.setShowSpinner(true);
    const storeResponse: IStoreResponse | IErrorResponse = await dispatch(
      actions.reInitiateStore, {}, contextStore.token, { id }
    );
    if ('error' in storeResponse) { contextStore.setShowSpinner(false); return; }
    setContextStore((past: any) => ({ ...past, store: storeResponse }));
    contextStore.setShowSpinner(false);
  };

  const onClickStopStore = async () => {
    contextStore.setShowSpinner(true);
    const stopResponse: IStoreResponse | IErrorResponse = await dispatch(
      actions.stopStore, {}, contextStore.token, { storeId: id }
    );
    if ('error' in stopResponse) { contextStore.setShowSpinner(false); return; }
    setContextStore((past: any) => ({ ...past, store: stopResponse }));
    contextStore.setShowSpinner(false);
  };

  const onClickDeleteStore = async () => {
    contextStore.setShowSpinner(true);
    const deleteStoreResponse: IDeleteResponse | IErrorResponse = await dispatch(
      actions.deleteStore, {}, contextStore.token, { id }
    );
    if ('error' in deleteStoreResponse) { contextStore.setShowSpinner(false); return; }
    setContextStore((past: any) => ({ ...past, store: {} as IStoreResponse }));
    contextStore.socket.emit('leaveStoreLogsRoom', [id]);
    contextStore.setShowSpinner(false);
    navigate('/dashboard/0');
  };

  useEffect(() => {
    (async () => {
      if (id !== '0') {
        if (contextStore.socket.connected) {
          contextStore.socket.emit('joinStoreLogsRoom', [id]);
          contextStore.socket.on('logs', (log: ILogResponse) => {
            const vLogs = logsRef.current;
            vLogs.push(log);
            setLogs(vLogs);
          });
        }
        const logs: Array<ILogResponse> | IErrorResponse = await dispatch(
          actions.getLogs, {}, contextStore.token, { storeId: id }
        );
        if ('error' in logs) return;
        logsRef.current = logs;
        setContextStore((past: any) => ({ ...past, logs }));
      }
      const storesResponse: Array<IStoreResponse> | IErrorResponse =
        await dispatch(actions.getStores, {}, contextStore.token);
      if ('error' in storesResponse) return;
      setStores(storesResponse);
    })();
    return () => {
      if (id !== '0') contextStore.socket.emit('leaveStoreLogsRoom', [id]);
      contextStore.socket.off('logs');
      setContextStore((past: any) => ({ ...past, store: {} as IStoreResponse }));
    };
  }, [id]);

  useEffect(() => {
    if (logsRef.current.length > 0) {
      if (logsRef.current[logsRef.current.length - 1].type === 'update') {
        return setContextStore((past: any) => ({
          ...past, logs: logsRef.current, storeUpdate: contextStore.storeUpdate + 1,
        }));
      }
      setContextStore((past: any) => ({ ...past, logs: logsRef.current }));
    }
  }, [logsUpdate]);

  // ─── VISUAL REDESIGN — logic above is 100% unchanged ────────────────────
  return (
    <Fragment>
      <header className="w-full border-b border-border bg-card shadow-sm">
        <div className="flex flex-wrap items-center gap-2 px-4 py-2 lg:px-6">

          {/* Brand */}
          <div className="mr-2 flex items-center gap-1 font-bold text-lg shrink-0">
            <span style={{ color: '#F68B1E' }}>Order</span>
            <span style={{ color: '#BA6918' }}>Bot</span>
          </div>

          {/* Active store badge */}
          <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-1.5 text-sm font-medium text-foreground">
            <span>{contextStore.store.name || 'All Stores'}</span>
            {contextStore.store.name && (
              <span
                className="h-2 w-2 rounded-full shrink-0"
                style={{
                  backgroundColor: contextStore.store.status ? '#22c55e' : '#ef4444',
                  boxShadow: contextStore.store.status ? '0 0 5px #22c55e' : '0 0 5px #ef4444',
                }}
              />
            )}
          </div>

          {/* Store dropdown */}
          <div className="relative shrink-0">
            <button
              onClick={() => setExpandDropdown(!expandDropdown)}
              className="flex items-center gap-1 rounded-lg border border-border bg-background px-3 py-1.5 text-sm font-medium text-foreground hover:bg-accent transition-colors"
            >
              <span>Available Shops</span>
              {expandDropdown ? <ArrowDropUpIcon fontSize="small" /> : <ArrowDropDownIcon fontSize="small" />}
            </button>

            {expandDropdown && (
              <div className="absolute left-0 top-full z-50 mt-1 w-72 rounded-xl border border-border bg-card shadow-dropdown overflow-hidden">
                {/* Store list */}
                <div className="max-h-64 overflow-y-auto p-2 space-y-1">
                  {stores.length === 0 && (
                    <p className="px-3 py-4 text-center text-sm text-muted-foreground">No stores found</p>
                  )}
                  {stores.map((store) => (
                    <div key={store._id} className="flex items-center justify-between gap-2 rounded-lg px-3 py-2 hover:bg-accent transition-colors">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span
                          className="h-2 w-2 rounded-full shrink-0"
                          style={{ backgroundColor: store.status ? '#22c55e' : '#ef4444' }}
                        />
                        <span className="text-sm font-medium text-foreground truncate">{store.name}</span>
                      </div>
                      <button
                        onClick={() => onSelectStore(store._id)}
                        className="shrink-0 rounded-md bg-primary px-2.5 py-1 text-xs font-semibold text-white hover:bg-primary/90 transition-colors"
                      >
                        Select
                      </button>
                    </div>
                  ))}
                </div>
                {/* Add store */}
                <div className="border-t border-border p-2">
                  <button
                    onClick={() => { setShowModal(true); setExpandDropdown(false); }}
                    className="w-full rounded-lg bg-primary/10 px-3 py-2 text-sm font-semibold text-primary hover:bg-primary/20 transition-colors"
                  >
                    + Add Store
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Click-away to close dropdown */}
          {expandDropdown && (
            <div className="fixed inset-0 z-40" onClick={() => setExpandDropdown(false)} />
          )}

          {/* Navigation buttons */}
          <div className="flex flex-wrap items-center gap-1.5 flex-1">
            <button onClick={onClickSmartAnalytics}
              className="rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-primary hover:text-white hover:border-primary transition-all">
              Smart Analytics
            </button>
            <button onClick={onClickProductGroup}
              className="rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-primary hover:text-white hover:border-primary transition-all">
              All Products
            </button>
            <button onClick={onClickProductGroupOrderDate}
              className="rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-primary hover:text-white hover:border-primary transition-all">
              Products (Order Date)
            </button>
          </div>

          {/* Store-specific actions + logout — right side */}
          <div className="flex items-center gap-1.5 ml-auto shrink-0">
            {contextStore.store._id && (
              <>
                <button onClick={onClickReinitiate}
                  className="rounded-lg border border-blue-300 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100 transition-colors dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-400">
                  Re Initiate
                </button>
                <button onClick={onClickStopStore}
                  className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-100 transition-colors dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-400">
                  Stop Store
                </button>
                <button onClick={onClickDeleteStore}
                  className="rounded-lg border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100 transition-colors dark:border-red-800 dark:bg-red-950/30 dark:text-red-400">
                  Delete
                </button>
              </>
            )}
            <button onClick={onClickLogout}
              className="rounded-lg border border-red-300 bg-red-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-600 transition-colors">
              Logout
            </button>
          </div>
        </div>
      </header>

      {showModal && <AddStoreModal setShowModal={setShowModal} />}
    </Fragment>
  );
};
